import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import '../../styles/App.css'
import Header from "../tables/dashboard/Header";

const aboutContent = {
  header: {
    hindi: "भारती संग्रह के बारे में",
    english: "About Bharati Sangraha"
  },

  intro: {
    hindi:
      "'भारती संग्रह' भारत की भाषाई विविधता को डिजिटल युग में सशक्त बनाने की दिशा में एक महत्त्वपूर्ण राष्ट्रीय पहल है। यह 'भारती बहुभाषी अनुवाद सारथी' सॉफ्टवेयर के अंतर्गत विकसित एक उन्नत बहुभाषी डेटा मंच है।",
    english:
      "Bharati Sangraha is a transformative national initiative under the Bharati Bahubhashi Anuvaad Sarthi program, aimed at empowering Indian languages in the digital era."
  },

  vision: {
    title: {
      hindi: "🌐 राष्ट्रीय विज़न",
      english: "🌐 National Vision"
    },
    description: {
      hindi:
        "कृत्रिम बुद्धिमत्ता, मशीन अनुवाद, वाक् प्रौद्योगिकी और NLP के माध्यम से भारतीय भाषाओं को डिजिटल सेवाओं में सक्षम बनाना।",
      english:
        "To enable Artificial Intelligence, Machine Translation, Speech Technologies, and NLP across Indian languages for inclusive digital services."
    }
  },

  objectives: {
    title: {
      hindi: "🎯 प्रमुख उद्देश्य",
      english: "🎯 Core Objectives"
    },
    points: {
      hindi: [
        "केंद्रीकृत एवं सुरक्षित राष्ट्रीय भाषा डेटा भंडार स्थापित करना।",
        "मानकीकृत डेटा अधिग्रहण ढांचा विकसित करना।",
        "बहु-स्तरीय सत्यापन द्वारा गुणवत्ता सुनिश्चित करना।",
        "मॉडल-रेडी और विश्वसनीय डेटा उपलब्ध कराना।"
      ],
      english: [
        "Establish a centralized and secure national linguistic repository.",
        "Develop standardized data acquisition frameworks.",
        "Ensure quality through multi-stage validation workflows.",
        "Deliver reliable and model-ready datasets."
      ]
    }
  },

  keyFeatures: {
    title: {
      hindi: "✨ प्रमुख विशेषताएँ",
      english: "✨ Key Features"
    },
    points: {
      hindi: [
        "VAPT प्रमाणित एंटरप्राइज़-ग्रेड सुरक्षा।",
        "15 भारतीय भाषाओं सहित अंग्रेज़ी का समर्थन।",
        "TTS, ASR, TTT एवं शब्दकोश मॉड्यूल।",
        "स्केलेबल एवं केंद्रीकृत राष्ट्रीय कॉर्पस प्लेटफ़ॉर्म।"
      ],
      english: [
        "Enterprise-grade security with VAPT verification.",
        "Supports English and 15 Indian languages, representing the first language of various States and Union Territories.",
        "Modules for TTS, ASR, Text Translation, and Dictionary.",
        "Centralized and scalable national corpus platform."
      ]
    }
  },

  beneficiaries: {
    title: {
      hindi: "👥 लाभार्थी एवं प्रभाव",
      english: "👥 Beneficiaries & Impact"
    },
    description: {
      hindi:
        "यह मंच छात्रों, शोधकर्ताओं, सरकारी संस्थानों एवं एआई डेवलपर्स को सहयोगात्मक पारिस्थितिकी तंत्र प्रदान करता है और नागरिकों को उनकी भाषाओं में बेहतर डिजिटल सेवाएँ उपलब्ध कराता है।",
      english:
        "The platform supports students, researchers, government institutions, and AI developers while enabling better digital services for citizens in their preferred languages."
    }
  },

  closing: {
    title: {
      hindi: "भारत की भाषाई शक्ति को डिजिटल भविष्य से जोड़ना",
      english: "Connecting India’s Linguistic Strength to the Digital Future"
    },
    button: {
      hindi: "लॉगिन पृष्ठ पर वापस जाएँ",
      english: "Back to Login"
    },
    description: {
    hindi: "एक सुरक्षित और भविष्य उन्मुख बहुभाषी डेटा पारिस्थितिकी तंत्र।",
    english: "A secure and future-ready multilingual data ecosystem."
  },
  }
};


const AboutSangraha = () => {
  const navigate = useNavigate();
  const { selectedLanguage } = useSelector((state) => state.loginPage);
  const isHindi = selectedLanguage === "hindi";
  const fontFamily = isHindi
    ? 'font-hindi'
    : 'font-english';

  const headingSize = isHindi ? "text-[43px]" : "text-[40px]";
  const paragraphSize = isHindi ? "text-[22px]" : "text-[18px]";
  const subheadingSize = isHindi ? "text-[20px]" : "text-[22px]";


  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 flex flex-col items-center justify-center px-6 py-16">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl text-center mb-16"
        >
          <div className="flex justify-center items-center gap-3 mb-3">
            <h1 style={{ fontFamily }} className={`${headingSize} text-5xl font-bold text-gray-900 mb-6`}>
              {aboutContent.header[selectedLanguage]}
            </h1>
          </div>
          <p style={{ fontFamily }} className={`${paragraphSize} text-lg text-gray-700 leading-relaxed`}>
            {aboutContent.intro[selectedLanguage]}
          </p>
        </motion.div>

        {/* Info Cards Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl w-full">

          {/* Vision */}
          <motion.div whileHover={{ scale: 1.03 }} className="bg-white shadow-lg rounded-2xl p-8 border border-orange-100">
            <h2 className="text-2xl font-bold text-[#ec7026] mb-4">
              {aboutContent.vision.title[selectedLanguage]}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {aboutContent.vision.description[selectedLanguage]}
            </p>
          </motion.div>

          {/* Objectives */}
          <motion.div whileHover={{ scale: 1.03 }} className="bg-white shadow-lg rounded-2xl p-8 border border-orange-100">
            <h2 className="text-2xl font-bold text-[#ec7026] mb-4">
              {aboutContent.objectives.title[selectedLanguage]}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              {aboutContent.objectives.points[selectedLanguage].map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>
          </motion.div>

          {/* Key Features */}
          <motion.div whileHover={{ scale: 1.03 }} className="bg-white shadow-lg rounded-2xl p-8 border border-orange-100">
            <h2 className="text-2xl font-bold text-[#ec7026] mb-4">
              {aboutContent.keyFeatures.title[selectedLanguage]}
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              {aboutContent.keyFeatures.points[selectedLanguage].map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>
          </motion.div>

          {/* Beneficiaries */}
          <motion.div whileHover={{ scale: 1.03 }} className="bg-white shadow-lg rounded-2xl p-8 border border-orange-100">
            <h2 className="text-2xl font-bold text-[#ec7026] mb-4">
              {aboutContent.beneficiaries.title[selectedLanguage]}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {aboutContent.beneficiaries.description[selectedLanguage]}
            </p>
          </motion.div>

        </div>


        {/* Closing Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 text-center max-w-3xl"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8">
            {aboutContent.closing.title[selectedLanguage]}
          </h3>
          <p style={{ fontFamily }} className="text-gray-700 font-['Semantic/Strong'] leading-relaxed mb-10">
            {aboutContent.closing.description[selectedLanguage]}
          </p>

          {/* Back to Login Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-gradient-to-r from-[#ec7026] to-orange-600 text-white font-semibold rounded-full shadow-md hover:shadow-lg transition-all duration-300"
          >
            {aboutContent.closing.button[selectedLanguage]}
          </motion.button>
        </motion.div>
      </div>
    </>
  );
};

export default AboutSangraha;
